import { FC, useCallback, useMemo } from 'react'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/PhoneStepNumberForm/PhoneStepNumberForm.styl'
import { Field, reduxForm, submit } from 'redux-form'
import { ReduxFormProps } from 'interfaces/component'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { asyncValidator } from 'helpers/schemaHelpers'
import { shouldAsyncValidate } from 'helpers/formHelpers'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { phoneFormSchema, CC_ONBOARDING_FORM_PHONE } from 'components/features/creditCard/creditCardOnboarding/constants'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { CreateCCPhoneAction } from 'components/features/creditCard/creditCardStore/actions'
import { useNextStep } from 'components/features/creditCard/creditCardOnboarding/useNextStep'
import { FormCommonError } from 'components/features/creditCard/creditCardOnboarding/FormCommonError/FormCommonError'
import { pushActionCreator } from 'actions/navigationActions'
import { useSMSRateLimit } from 'components/features/creditCard/creditCardOnboarding/useSMSRateLimit'
import { getTimeLeftDisplay } from 'components/features/creditCard/creditCardOnboarding/helpers'
import { useStepNumber } from 'components/features/creditCard/creditCardOnboarding/useStepNumber'


const PHONE_PROPS = {
  label: 'Phone number',
  theme: 'snow',
  placeholder: '(___) ___ - ____',
  qaMarker: 'phone-input',
  type: 'tel',
  inputmode: 'numeric',
  mask: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, ' ', '-', ' ', /\d/, /\d/, /\d/, /\d/],
  autoComplete: 'off',
  pattern: '^\(\d{3}\) \d{3} - \d{4}$',
}

type FormValues = {
  phone_number: string
}

const EMPTY_OBJ = {}
export const PhoneStepNumberForm: FC = () => {
  const nextStep = useNextStep()
  const dispatch = useStoreDispatch()
  const handleSubmit = useCallback<(values: FormValues) => {}>((values) => {
    const { phone_number } = values
    const phoneNumber = `+1${ phone_number.replace(/\D+/g, '') }`

    dispatch(new CreateCCPhoneAction({
      person_data: {
        phone_number: phoneNumber,
      },
      step: nextStep || 'ssn',
    }))

    return EMPTY_OBJ
  }, [dispatch, nextStep])

  const handleNextButton = useCallback(() => {
    dispatch(submit(CC_ONBOARDING_FORM_PHONE))
  }, [dispatch])

  const formChild = useMemo(() => (
    <>
      <div className={ styles.note }>
        We’ll send you a code to verify this number.
      </div>
      <FormWrapped onSubmit={ handleSubmit } />
    </>
  ), [handleSubmit])

  const isLoading = useStoreSelector((state) => state.app.creditCard.createPhoneRequest.isLoading)
  const secondsLeft = useSMSRateLimit()

  const handleBack = useCallback(() => {
    dispatch(pushActionCreator(`/credit-card/onboarding/${ nextStep }`))
  }, [dispatch, nextStep])

  const stepNo = useStepNumber('phone')

  return (
    <OnboardingPagePreset
      step={ stepNo }
      title='Confirm your mobile number'
      card={ formChild }
      onBack={ !!nextStep ? handleBack : undefined }
      progressPercent={ 20 }
    >

      <Button
        text={ secondsLeft ? `Get code in ${ getTimeLeftDisplay(secondsLeft) }` : 'Get code' }
        onClick={ handleNextButton }
        fullWidth
        qaMarker={ secondsLeft ? `get-code-in-${ secondsLeft }-sec` : 'get-code' }
        disabled={ !!secondsLeft && secondsLeft > 0 }
        isSubmitting={ isLoading }
      />
      <Link to='/credit-card'>
        <Button
          text='Finish later'
          style='textOnly'
          fullWidth
          qaMarker='finish-later'
        />
      </Link>

      <div className={ styles.bottomNote } data-qa-marker='footnotes'>
        Your phone number may be used for customer communications, fraud prevention and two-factor authentication. Standard carrier rates may be charged.
      </div>
    </OnboardingPagePreset>
  )
}

type FormProps =
  & ReduxFormProps<FormValues>
  & {}

const Form: FC<FormProps> = (props) => {
  const { handleSubmit, error } = props

  return (
    <form onSubmit={ handleSubmit }>
      <ul className={ styles.controlList }>
        <li className={ styles.controlListItem }>
          <Field
            name='phone_number'
            component={ TextFieldWrapped }
            props={ PHONE_PROPS }
          />
        </li>
        <FormCommonError error={ error } className={ styles.controlListItem } />
        <br />
      </ul>
    </form>
  )
}

const FormWrapped = reduxForm<FormValues, FormProps>({
  form: CC_ONBOARDING_FORM_PHONE,
  asyncValidate: asyncValidator(phoneFormSchema),
  shouldAsyncValidate,
})(Form)
